import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// @ts-ignore: TS2459 — we know it's there at runtime
import { ClassesGridSection } from '@PikaDoApp/pikado-web-dsm';
import { setSelectedClass } from '../../redux/actions/class';
import { addClassMonthlyReportArray, setSelectedClassMonthlyReportMonth } from '../../redux/actions/fullReport';
import { reducersState } from '../../redux/reducers';
import { SchoolStatisticServiceWrapper, ClassMonthlyReport } from '../Report/SchoolReport/SchoolStatisticServiceWrapper';

const TEACHER_PLACEHOLDER_IMAGE = '/assets/teacher-placeholder.png';

const CLASSES_LEGEND_ITEMS = [
  { status: 'good' as const, label: 'Good' },
  { status: 'accepted' as const, label: 'Accepted' },
  { status: 'bad' as const, label: 'Bad' },
];

interface ClassesGridSectionContainerProps {
  classes: any[];
  monthOptions: Array<{label: string; value: string}>;
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  showSchoolData: boolean;
  schoolId: string;
}

export const ClassesGridSectionContainer: React.FC<ClassesGridSectionContainerProps> = ({
  classes,
  monthOptions,
  selectedMonth,
  onMonthChange,
  showSchoolData,
  schoolId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get class monthly reports cache from Redux
  const classMonthlyReportsCache = useSelector(
    (state: reducersState) => state.fullReport?.classMonthlyReportsCache || []
  );

  // Fetch class monthly reports and cache them in Redux
  useEffect(() => {
    if (!schoolId || !selectedMonth || !showSchoolData) {
      return;
    }

    // Check if the selected month exists in Redux cache
    const existingCache = classMonthlyReportsCache.find(
      (item) => item.month === selectedMonth
    );

    if (existingCache) {
      // Month exists in cache, just update selection if not already selected
      if (!existingCache.selected) {
        dispatch(setSelectedClassMonthlyReportMonth(selectedMonth));
      }
    } else {
      // Month doesn't exist in cache, fetch from API
      const fetchData = async () => {
        try {
          const service = new SchoolStatisticServiceWrapper();
          const reports = await service.getClassesMonthlyReportsBySchoolIdAndMonth(schoolId, selectedMonth);

          // Add reports to Redux cache with selected = true
          dispatch(addClassMonthlyReportArray(selectedMonth, reports || [], true));
        } catch (error) {
          console.error('Error fetching class monthly reports for month:', selectedMonth, error);
          // Add empty array to cache on error to prevent repeated failed requests
          dispatch(addClassMonthlyReportArray(selectedMonth, [], true));
        }
      };

      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, selectedMonth, showSchoolData, dispatch]);

  // Get the selected month's reports from Redux cache
  const selectedReports = useMemo(() => {
    const selectedCache = classMonthlyReportsCache.find((item) => item.month === selectedMonth)
      || classMonthlyReportsCache.find((item) => item.selected);
    return selectedCache?.reports || [];
  }, [classMonthlyReportsCache, selectedMonth]);

  // Transform reports to card format
  const classesData = useMemo(() => {
    const reportByClassId = new Map(
      (selectedReports || []).map((report: any) => [report.classId, report])
    );

    const classesToDisplay = classes?.length
      ? classes
      : selectedReports.map((report: any) => ({
          id: report.classId,
          name: report.class?.name || report.className,
          students: [],
        }));

    return classesToDisplay.map((classItem: any) => {
      const report = reportByClassId.get(classItem.id) as ClassMonthlyReport | undefined;
      // Validate performance value to prevent NaN
      const performance = report && Number.isFinite(report.classMonthlyAverage)
        ? report.classMonthlyAverage
        : 0;
      const status = performance >= 40 ? 'good' : performance >= 5 ? 'accepted' : 'bad';
      const reportClass = report as any;
      const rosterStudentCount = classItem.students?.length;
      const studentCountFromReport = reportClass?.studentsCountInClassThisMonth;
      const studentCount = Number.isFinite(rosterStudentCount)
        ? rosterStudentCount
        : Number.isFinite(studentCountFromReport)
          ? studentCountFromReport
          : 0;

      return {
        id: classItem.id || reportClass?.classId,
        name: classItem.name || reportClass?.class?.name || reportClass?.className || 'Unnamed Class',
        studentCount,
        teacherAvatar: reportClass?.class?.teacher?.profilePicture?.fileUri || TEACHER_PLACEHOLDER_IMAGE,
        teacherName: reportClass?.class?.teacher?.name || classItem.teacher?.name || 'No Teacher',
        status: status,
      };
    });
  }, [classes, selectedReports]);

  const handleClassClick = (classId: string) => {
    dispatch(setSelectedClass(classId));
    navigate(`/school/${schoolId}/detailed`);
  };

  return (
    <ClassesGridSection
      classes={classesData}
      monthOptions={monthOptions}
      selectedMonth={selectedMonth}
      onMonthChange={onMonthChange}
      legendItems={CLASSES_LEGEND_ITEMS}
      onClassClick={handleClassClick}
    />
  );
};
